import type { CSSProperties } from 'react'
import { Check, ChevronLeft, ChevronRight, Filter, Plus, Search, X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import type { CounterGrammar } from '../utils/deckCardNavigation'

interface Props {
  /** Leaves editing mode — the only way out, matching the bar's only way in. */
  onDone: () => void
  /** Appends a new card in place and focuses its Term (brief §10). Never
   * inert: it is the phone's way to create a card. */
  onAddCard: () => void
  /** Brief §13 — moves to the adjacent card of the SHOWN set, carrying the
   * caret's field type with it. Inert at the ends; live with no active card,
   * where a press activates the first card (§4.2). */
  onPrev: () => void
  onNext: () => void
  canPrev: boolean
  canNext: boolean
  /** 1-based position of the active card in the shown set; null renders the
   * dash form `— OF 150`, which §4.2 specifies for "no active card". */
  position: number | null
  total: number
  grammar: CounterGrammar
  /** Tapping the counter opens quick jump (§13). */
  onOpenJump: () => void
  /** Leaves the `found` state. Only rendered for that grammar — the × is the
   * search state's own exit, and the filter state deliberately has none. */
  onClearSearch: () => void
}

/**
 * The deck screen's pinned bottom bar in editing mode (ADR-067 Decision 2,
 * narrowed by Decision B), now in its full three-zone shape (brief §4.2):
 *
 *     [ + Card ]   [ ‹  42 OF 150  › ]   [ Done ]
 *
 * `+ Field` and `Fill` are not here — both act on exactly one card, so both
 * live on the active card itself (ADR-067 Decision B). What is left is
 * deck-scoped by construction: `+ Card` appends to the deck regardless of which
 * (if any) card is active, the counter and its arrows walk the deck, and `Done`
 * leaves editing mode. None of the three ever needs a disabled state — the
 * arrows go inert at the ends of the set, which is a boundary rather than a
 * scope mismatch, and the disabled `+ Field` this bar used to render was the
 * signal that a card-scoped control did not belong here.
 *
 * Renders the bar's own body only — positioning and the single shared height
 * measurement belong to the caller (DeckMobileFeed), same convention as
 * DeckViewBar and SelectModeActionBar.
 */
export default function DeckEditBar({
  onDone, onAddCard, onPrev, onNext, canPrev, canNext,
  position, total, grammar, onOpenJump, onClearSearch,
}: Props) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  // The extra × (clear search) button only exists for `grammar === 'found'`
  // (line ~115 below), widening the nav zone by roughly one tap target's
  // worth. That is the one thing that changes how much room `+ Card` has —
  // nothing else in this bar varies with search — so the narrower drop
  // threshold below is scoped to this grammar alone, not to every render.
  const searchNarrowing = grammar === 'found'

  return (
    <>
    {/* Layer 2 of the degrade order below — same inline-`<style>` +
        `max-width` media query technique already used in DeckDetailPage for
        `.card-row-move-buttons`, not a new mechanism. The threshold is a
        provisional estimate (bar padding + gaps + icon-only `+ Card` +
        the four-button nav zone `grammar === 'found'` adds + `Done`, summed
        against common small-Android viewport widths), owed confirmation by
        the required on-device smoke at "the editing bar at its narrowest"
        before this is trusted as tuned rather than merely reasoned about. */}
    <style>{`
      @media (max-width: 420px) {
        .deck-edit-bar-addcard-searching { display: none; }
      }
    `}</style>
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px calc(8px + env(safe-area-inset-bottom, 0px))',
        // Editing-mode plane (DESIGN_SYSTEM.md Wave 4 §7 + its editing variant) —
        // opaque and warning-tinted, distinct from both the viewing bar's
        // --bg-chrome and the multi-select bar's --bg-chrome-select, so the mode
        // reads at a glance in either theme, the way the selection bar does.
        background: 'var(--bg-chrome-edit)',
        borderTop: '1px solid var(--border-chrome-edit)',
        boxShadow: 'var(--shadow-chrome-above)',
      }}
    >
      {/* §4.2's degrade order now runs in two layers rather than one, both
          CSS-only — no width measurement, no second render pass.
          Layer 1 (any width, unconditional): the LABEL gives way first —
          `overflow:hidden` on the label already gives it a min-content of 0,
          so flexbox is free to squeeze it to nothing while `flexShrink:0`
          below keeps the icon out of that same squeeze. This is the actual
          fix for the reported bug: the icon used to be the thing that
          absorbed the deficit once the label had nothing left to give —
          `flexShrink` has no floor of its own, so a shrinkable SVG child
          just renders smaller and smaller until it reads as an empty circle
          with no glyph, which is worse than showing nothing: a control that
          LOOKS present but shows nothing is a broken affordance, not a
          degraded one.
          Layer 2 (`grammar === 'found'` only, below the media query's
          threshold): the whole button gives way, via
          `.deck-edit-bar-addcard-searching` in the `<style>` below. The
          extra × (clear search) button that only exists for this grammar is
          the one thing that narrows the bar beyond what layer 1 alone can
          absorb — icon + its own padding still needs real room once the
          label is gone, and at that point dropping the control is the
          honest outcome, not a second attempt to shrink what layer 1 already
          reduced to its floor. */}
      <button
        onClick={onAddCard}
        aria-label={dd.barAddCardAria}
        className={searchNarrowing ? 'deck-edit-bar-addcard-searching' : undefined}
        style={barControlStyle}
      >
        <Plus size={17} strokeWidth={2.2} style={{ flexShrink: 0 }} />
        <span style={labelTextStyle}>{dd.barAddCard}</span>
      </button>

      <div style={{ flex: 1, minWidth: 0 }} />

      {/* §13's navigator. One unit: the arrows and the count they move through
          read as a single control rather than three neighbours that happen to
          be adjacent. */}
      <div style={navZoneStyle}>
        <button
          onClick={onPrev}
          disabled={!canPrev}
          aria-label={dd.navPrevAria}
          style={arrowStyle(canPrev)}
        >
          <ChevronLeft size={18} strokeWidth={2.4} />
        </button>

        <button
          onClick={onOpenJump}
          aria-label={dd.navJumpAria}
          style={counterStyle(grammar)}
        >
          {grammar === 'shown' && <Filter size={11} strokeWidth={2.4} />}
          {grammar === 'found' && <Search size={11} strokeWidth={2.4} />}
          <span style={counterTextStyle}>
            {/* The dash form, not a zero and not a blank: §4.2 asks the counter
                to keep saying how big the deck is while saying that nothing in
                it is being edited. */}
            {position === null ? dd.navCounterDash(total) : dd.navCounter(position, total)}
            {grammar === 'shown' && <span style={qualifierStyle}>{dd.navShown}</span>}
            {grammar === 'found' && <span style={qualifierStyle}>{dd.navFound}</span>}
          </span>
        </button>

        {/* The search state carries its own exit; the filter state deliberately
            does not (§13 — the filter has a backing because it really narrowed
            the deck, and it is dismissed where it was set). */}
        {grammar === 'found' && (
          <button onClick={onClearSearch} aria-label={dd.navClearSearchAria} style={arrowStyle(true)}>
            <X size={15} strokeWidth={2.4} />
          </button>
        )}

        <button
          onClick={onNext}
          disabled={!canNext}
          aria-label={dd.navNextAria}
          style={arrowStyle(canNext)}
        >
          <ChevronRight size={18} strokeWidth={2.4} />
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 0 }} />

      <button
        onClick={onDone}
        aria-label={dd.barExitEditingAria}
        style={{
          flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          height: 44, padding: '0 20px',
          background: 'var(--control-solid)', color: 'var(--control-solid-text)',
          border: 'none', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
          fontFamily: 'var(--font-ui)', fontSize: 14.5, fontWeight: 600,
        }}
      >
        <Check size={17} strokeWidth={2.2} />
        <span>{dd.doneBtn}</span>
      </button>
    </div>
    </>
  )
}

// §4.2's degradation order, unchanged by the third zone: the label text of
// `+ Card` is what gives way first, and nothing after it holds an
// intermediate stage — the counter and `Done` never shrink at all. At most
// widths this is where it stops: the icon survives, and a narrowed bar reads
// as icons rather than wrapping to a second row. Only when search's own
// extra × button (`grammar === 'found'`) leaves no room even for that does
// `+ Card` give way entirely, via the media query above — see the comment on
// the button itself for why an icon squeezed smaller than its own glyph is
// worse than the control being absent. (`+ Field` is no longer here to
// degrade; ADR-067 Decision B moved it.)
const labelTextStyle: CSSProperties = {
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const barControlStyle: CSSProperties = {
  flexShrink: 1, minWidth: 44,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 44, padding: '0 16px',
  background: 'transparent',
  border: '1px solid var(--border-chrome-edit)',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600,
}

// Never shrinks — the counter is the one control that must stay legible when
// the bar narrows, since it is the only thing saying where in the deck the
// author is.
const navZoneStyle: CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', gap: 2,
}

const arrowStyle = (enabled: boolean): CSSProperties => ({
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 34, height: 44,
  background: 'transparent', border: 'none', padding: 0,
  // Inert rather than gone: an arrow that vanishes at the end of the deck moves
  // the two beside it, and the reader loses the fixed target they were tapping.
  color: enabled ? 'var(--text-primary)' : 'var(--text-secondary)',
  opacity: enabled ? 1 : 0.4,
  cursor: enabled ? 'pointer' : 'default',
})

// The backing is the filter state's alone. §13: a filter genuinely narrowed the
// deck, so it is marked; a search narrowed only the walk, so it is not.
const counterStyle = (grammar: CounterGrammar): CSSProperties => ({
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  height: 34, padding: grammar === 'plain' ? '0 6px' : '0 9px',
  background: grammar === 'shown' ? 'var(--accent-soft)' : 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  color: grammar === 'plain' ? 'var(--text-secondary)' : 'var(--text-primary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 700,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
})

const counterTextStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'baseline', gap: 4,
}

const qualifierStyle: CSSProperties = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
  color: 'var(--text-secondary)',
}
